import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '../components/ui/ui-button';
import { getMe, getBillingInfo, logout } from '../api/client';
import type { UserMe, BillingInfo } from '../api/types';
import { ApiClientError } from '../api/types';
import { motionStyles, statStyles, boxSizingStyles, responsiveStyles } from '../styles/lit-shared';
import { navigate } from '../router';

const PLAN_LABELS: Record<string, string> = {
  starter: 'Starter',
  growth: 'Growth',
  scale: 'Scale',
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Activo',
  canceling: 'Cancela al final del periodo',
  canceled: 'Cancelado',
  past_due: 'Pago pendiente',
};

@customElement('profile-page')
export class ProfilePage extends LitElement {
  static styles = [
    boxSizingStyles,
    responsiveStyles,
    motionStyles,
    statStyles,
    css`
    :host {
      display: block;
    }

    .hero {
      position: relative;
      margin-bottom: var(--space-2xl);
      padding: var(--space-xl);
      border-radius: var(--radius-lg);
      background: var(--gradient-primary-soft);
      border: 1px solid var(--color-border-glow);
      overflow: hidden;
    }

    .hero::before {
      content: '';
      position: absolute;
      top: -50%;
      right: -10%;
      width: 300px;
      height: 300px;
      border-radius: 50%;
      background: var(--glow-violet);
      filter: blur(50px);
      pointer-events: none;
    }

    .hero-content {
      position: relative;
      z-index: 1;
    }

    .hero-eyebrow {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      margin-bottom: var(--space-sm);
      padding: 0.25rem 0.75rem;
      border-radius: var(--radius-full);
      background: rgb(255 255 255 / 6%);
      border: 1px solid var(--color-border);
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--color-accent);
    }

    h1 {
      font-size: clamp(1.75rem, 4vw, 2.25rem);
      font-weight: 700;
      letter-spacing: -0.03em;
      margin-bottom: var(--space-xs);
      background: var(--gradient-text);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .subtitle {
      color: var(--color-text-muted);
      font-size: 1rem;
    }

    .info-card {
      padding: var(--space-lg);
      border-radius: var(--radius);
      background: var(--color-surface);
      border: 1px solid var(--color-border);
    }

    .info-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: var(--space-md) 0;
      border-bottom: 1px solid var(--color-border);
    }

    .info-row:last-child {
      border-bottom: none;
    }

    .info-label {
      font-size: 0.875rem;
      color: var(--color-text-muted);
      font-weight: 500;
    }

    .info-value {
      font-size: 0.9375rem;
      color: var(--color-text);
      font-weight: 600;
      text-align: right;
      word-break: break-all;
    }

    .info-value--highlight {
      background: var(--gradient-text);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .plan-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.25rem 0.5rem;
      border-radius: var(--radius-full);
      background: var(--gradient-primary-soft);
      border: 1px solid var(--color-border-glow);
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--color-primary-light);
    }

    .cta-section {
      margin-top: var(--space-xl);
      display: flex;
      justify-content: space-between;
      gap: var(--space-md);
    }

    .cta-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1.5rem;
      border-radius: var(--radius);
      font-size: 0.9375rem;
      font-weight: 600;
      cursor: pointer;
      border: none;
      transition: transform var(--duration-fast) var(--ease-out);
    }

    .cta-btn:hover {
      transform: translateY(-1px);
    }

    .cta-btn--primary {
      background: var(--gradient-primary);
      color: var(--palette-white);
      box-shadow: 0 4px 16px rgb(139 92 246 / 30%);
    }

    .loading-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: var(--space-md);
      padding: var(--space-2xl) 0;
      color: var(--color-text-muted);
    }

    .error-state {
      padding: var(--space-lg);
      border-radius: var(--radius);
      background: rgb(244 63 94 / 8%);
      border: 1px solid rgb(251 113 133 / 25%);
      color: var(--color-error);
      text-align: center;
    }

    @media (max-width: 639px) {
      .info-row {
        flex-direction: column;
        align-items: flex-start;
        gap: var(--space-xs);
      }

      .info-value {
        text-align: left;
      }

      .cta-section {
        flex-direction: column;
        align-items: stretch;
      }

      .cta-btn {
        justify-content: center;
      }
    }
    `,
  ];

  @state() private user: UserMe | null = null;
  @state() private billing: BillingInfo | null = null;
  @state() private loading = true;
  @state() private loggingOut = false;
  @state() private error = '';

  connectedCallback() {
    super.connectedCallback();
    void this.loadData();
  }

  private async handleLogout() {
    this.loggingOut = true;
    this.error = '';

    try {
      await logout();
    } catch (err) {
      this.error = err instanceof ApiClientError ? err.message : 'Error al cerrar sesión';
      this.loggingOut = false;
    }
  }

  private async loadData() {
    this.loading = true;
    this.error = '';

    try {
      const [user, billing] = await Promise.all([getMe(), getBillingInfo()]);
      this.user = user;
      this.billing = billing;
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 401) return;
      this.error = err instanceof ApiClientError ? err.message : 'Error al cargar el perfil';
    } finally {
      this.loading = false;
    }
  }

  render() {
    if (this.loading) {
      return html`
        <div class="loading-state animate-fade-up">
          <div class="spinner" role="status" aria-label="Cargando"></div>
          <p>Cargando perfil…</p>
        </div>
      `;
    }

    if (this.error) {
      return html`<div class="error-state">${this.error}</div>`;
    }

    if (!this.user) return html``;

    const totalCredits = this.user.credits + this.user.extraCredits;
    const sub = this.billing?.subscription;

    return html`
      <div class="animate-fade-up">
        <section class="hero">
          <div class="hero-content">
            <span class="hero-eyebrow">✦ Mi perfil</span>
            <h1>Tu cuenta</h1>
            <p class="subtitle">Gestiona tu información y suscripción.</p>
          </div>
        </section>

        <div class="info-card">
          <div class="info-row">
            <span class="info-label">Email</span>
            <span class="info-value">${this.user.email}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Créditos de suscripción</span>
            <span class="info-value info-value--highlight">${this.user.credits}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Créditos extra</span>
            <span class="info-value info-value--highlight">${this.user.extraCredits}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Total disponible</span>
            <span class="info-value info-value--highlight">${totalCredits}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Plan</span>
            <span class="info-value">
              ${sub
                ? html`<span class="plan-badge">${PLAN_LABELS[sub.plan] ?? sub.plan}</span>`
                : 'Sin plan'}
            </span>
          </div>
          ${sub
            ? html`
              <div class="info-row">
                <span class="info-label">Estado</span>
                <span class="info-value">${STATUS_LABELS[sub.status] ?? sub.status}</span>
              </div>
            `
            : ''}
        </div>

        <div class="cta-section">
          <button
            class="cta-btn cta-btn--primary"
            @click=${() => void navigate('/credits')}
          >
            Créditos
          </button>
          <ui-button
            variant="danger"
            ?loading=${this.loggingOut}
            ?disabled=${this.loggingOut}
            @ui-click=${() => void this.handleLogout()}
          >
            Cerrar sesión
          </ui-button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'profile-page': ProfilePage;
  }
}
